<template>
	<div class="p-component p-fluid p-formgrid p-grid">
		<div class="p-col-3">
			<p style="font-size: 1rem;">{{ trainingKey.description }}</p>
		</div>
		<div class="p-field p-input-filled p-col-9">
			<InputText type="text" v-model="trainingKey.value.value" disabled />
			<small v-if="!(trainingKey.value.value !== '' && trainingKey.value.value !== null)" class="p-error">{{$t("Squeeze.Training.TrainingKey")}}</small>
		</div>

		<div class="p-col-3">
			<p style="font-size: 1rem;">{{ $t("Squeeze.Training.Indicator") }}</p>
		</div>
		<div class="p-field p-input-filled p-col-9">
			<Dropdown
				v-model="indicatorId"
				:options="allIndicators"
				optionLabel="description"
				optionValue="id"
				/>
		</div>

		<div class="p-col-3">
			<p style="font-size: 1rem;">{{ $t("Squeeze.Training.Description") }}</p>
		</div>
		<div class="p-col-9">
			<div class="p-field p-input-filled p-inputgroup">
				<InputText type="text" v-model="regionAnchor.description" />
			</div>
		</div>

		<div class="p-col-3">
			<p v-tooltip.top="regionAnchor.locatorId ? $t('Squeeze.Training.OptionalFallback') : $t('Squeeze.Training.Optional')" style="font-size: 1rem; cursor: help;">{{ $t("Squeeze.Training.KeyWord") }}</p>
		</div>
		<div class="p-col-9">
			<div class="p-field p-input-filled p-inputgroup">
				<InputText
					type="text"
					v-model="regionAnchor.keyWordPattern"
					:placeholder="$t('Squeeze.Training.KeyWord')"
					@focus="onFocusField('regionAnchorKeyWordPattern')"
				/>
				<Button class="active" icon="mdi mdi-auto-fix" @click="onFocusField('regionAnchorKeyWordPattern')" />
			</div>
		</div>

		<div class="p-col-3">
			<p v-tooltip.top="$t('Squeeze.Training.Optional')" style="font-size: 1rem; cursor: help;">{{ $t("Squeeze.Training.Locator") }}</p>
		</div>
		<div class="p-field p-input-filled p-col-9">
			<Dropdown
				v-model="regionAnchor.locatorId"
				:options="allLocators"
				optionLabel="description"
				optionValue="id"
				:showClear="true"
				:placeholder="$t('Squeeze.Training.SelectLocator')"
			/>
		</div>

		<div class="p-col-3">
			<p style="font-size: 1rem;">{{ $t("Squeeze.Training.Region") }}</p>
		</div>
		<div class="p-field p-input-filled p-col-9">
			<div class="p-inputgroup">
				<span
					v-if="regionBadge"
					class="p-inputtext p-component region-display region-display--set region-display--clickable"
					@click="markCurrentRegion"
				>✓ {{ regionBadge }}</span>
				<span
					v-else
					class="p-inputtext p-component region-display region-display--unset"
				>{{ $t('Squeeze.Training.RegionNotSet') }}</span>
				<Button icon="mdi mdi-vector-rectangle" @click="onFocusField('regionAnchorRegion')" />
			</div>
		</div>

		<div class="p-col-3">
			<p style="font-size: 1rem;">{{ $t("Squeeze.Training.IgnoreSpaces") }}</p>
		</div>
		<div class="p-field p-input-filled p-col-9">
			<Dropdown v-model="regionAnchor.ignoreWhiteSpaces" :options="booleanOptions" optionLabel="name" optionValue="id" />
		</div>

		<div class="p-col-3">
			<span v-tooltip.top="!canTrain ? $t('Squeeze.Training.PatternOrLocatorRequired') : null">
				<Button
					:label="regionAnchor.id ? $t('Squeeze.Training.Retrain') : $t('Squeeze.Training.Train')"
					:disabled="!canTrain"
					@click="trainRegionAnchor"
				/>
			</span>
		</div>
	</div><br/>

	<div class="inner-content">
		<FormPositionTrainingTable
			tableOrigin="regionAnchors"
			:tableData="allRegionAnchors"
			:loading="loading"
			@testData="testRegionAnchor"
			@changeData="changeRegionAnchor"
			@deleteData="deleteRegionAnchor"
			@onMarkRegion="onMarkRegion"
		/>
	</div>
</template>

<script lang="ts">
import {defineComponent, onBeforeMount, onMounted, PropType, reactive, ref, watch, computed} from "vue";
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import {DocumentField, FormTrainingItemRegionAnchor, FormTrainingRegionAnchor, DocumentLocator} from "@dex/squeeze-client-ts";
import {ClientManager} from "@/singletons/ClientManager";
import {ToastManager} from "@/util/ToastManager";
import InputText from "primevue/inputtext";
import Dropdown from "primevue/dropdown";
import Button from "primevue/button";
import FormPositionTrainingTable from "@/apps/squeeze/components/FormPositionTrainingTable.vue";

export default defineComponent({
	name: "FormTrainingRegionAnchorItem",
	components: {
		FormPositionTrainingTable,
		InputText,
		Dropdown,
		Button,
	},
	props: {
		trainingKeyField: {
			type: Object as PropType<DocumentField>,
			default: () => ({}),
		},
		documentClassId: {
			type: Number,
			required: true,
		},
		regionAnchorOfFormTraining: {
			type: Object as PropType<FormTrainingRegionAnchor>,
			default: () => ({}),
		},
		booleanOptions: {
			type: Array as PropType<any[]>,
			default: () => [],
		},
	},
	emits: [
		'testRegionAnchor',
		'onFocusField',
		'onMarkRegion',
	],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();

		/** Current training key field */
		const trainingKey = reactive<DocumentField>({});

		/** Selected Indicator ID */
		const indicatorId = ref<number>(0);

		/** Current regin anchor */
		const regionAnchor = reactive<FormTrainingRegionAnchor>({
			id: undefined,
			description: '',
			keyWordPattern: '',
			keyWordRegion: {
				page: 0,
				x0: 0,
				y0: 0,
				x1: 0,
				y1: 0,
			},
			ignoreWhiteSpaces: false,
			locatorId: undefined,
		});

		/** Show Loading on load data */
		const loading = ref(false);

		/** All region anchors */
		const allRegionAnchors = ref<FormTrainingRegionAnchor[]>([]);

		/** All indicators */
		const allIndicators = ref<FormTrainingItemRegionAnchor[]>([]);

		/** All locators */
		const allLocators = ref<DocumentLocator[]>([]);

		/** Compact display string for the current bounding box, null when not yet drawn */
		const regionBadge = computed(() => {
			const r = regionAnchor.keyWordRegion;
			if (!r || (r.x0 === 0 && r.y0 === 0 && r.x1 === 0 && r.y1 === 0)) return null;
			const fmt = (n: number | undefined) => Number((n ?? 0).toFixed(1));
			return `p.${r.page ?? 0} · (x0:${fmt(r.x0)}, y0:${fmt(r.y0)}) → (x1:${fmt(r.x1)}, y1:${fmt(r.y1)})`;
		});

		/** Train button guard — pattern or locator required, plus a drawn region */
		const canTrain = computed(() =>
			!!indicatorId.value
			&& (regionAnchor.keyWordPattern !== '' || !!regionAnchor.locatorId)
			&& !!regionBadge.value
			&& trainingKey.value?.value !== ''
			&& trainingKey.value?.value != null
		);

		/** Form Training API endpoint */
		const formTrainingApi = ClientManager.getInstance().squeeze.formTraining;

		/** Locator API endpoint */
		const locatorApi = ClientManager.getInstance().squeeze.locator;

		/**
		 * Triggered when a field is focused
		 * @param {string} fieldName
		 */
		const onFocusField = (fieldName: string) => {
			emit("onFocusField", fieldName);
		}

		/** Set first indicator */
		const setFirstIndicator = () => {
			if (allIndicators.value && allIndicators.value.length && allIndicators.value[0].id) {
				indicatorId.value = allIndicators.value[0].id;
			}
		}

		/** Reset region anchor */
		const resetRegionAnchor = () => {
			Object.assign(regionAnchor, {
				id: undefined,
				description: '',
				keyWordPattern: '',
				keyWordRegion: {
					page: 0,
					x0: 0,
					y0: 0,
					x1: 0,
					y1: 0,
				},
				ignoreWhiteSpaces: false,
				locatorId: undefined,
			});
		}

		/** Get all region anchors */
		const getAllRegionAnchors = () => {
			loading.value = true;

			formTrainingApi.getFormItemRegionAnchors(props.documentClassId, indicatorId.value)
				.then(data => {
					allRegionAnchors.value = data;
				}).catch(response => response.json().then ((err: { message: string; statusCode: number }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
				})).finally(() => {
					loading.value = false;
				});
		}

		/** Train or retrain a region anchor */
		const trainRegionAnchor = () => {
			loading.value = true;

			let promise;
			if (regionAnchor.id) {
				promise = formTrainingApi.retrainFormItemRegionAnchor(props.documentClassId, regionAnchor.id, regionAnchor, true);
			} else {
				promise = formTrainingApi.trainFormItemRegionAnchor(props.documentClassId, indicatorId.value, regionAnchor, false);
			}

			promise
				.then(() => {
					getAllRegionAnchors();
					resetRegionAnchor();
				}).catch((err: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), err.message);
				}).finally(() => {
					loading.value = false;
				});
		}

		/**
		 * Test a region anchor
		 * @param rowData
		 */
		const testRegionAnchor = (rowData: any) => {
			rowData.indicatorId = indicatorId.value;
			emit("testRegionAnchor", rowData);
		}

		/**
		 * Change a region anchor
		 * @param data
		 */
		const changeRegionAnchor = (data: any) => {
			Object.assign(regionAnchor, { locatorId: undefined, ...data });
		}

		/**
		 * Deletes a region anchor
		 * @param data
		 */
		const deleteRegionAnchor = (data: any) => {
			loading.value = true;
			formTrainingApi.deleteFormItemRegionAnchor(props.documentClassId, data.id)
				.then(() => {
					getAllRegionAnchors();
					resetRegionAnchor();
				}).catch(response => response.json().then ((err: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
				})).finally(() => {
					loading.value = false;
				});
		}

		/** Get all Indicators */
		const getAllIndicators = () => {
			loading.value = true;
			formTrainingApi.getFormTableItemIndicator(props.documentClassId, trainingKey.value!.value!)
				.then(data => {
					allIndicators.value = data;
					setFirstIndicator();
				}).catch(response => response.json().then ((err: { message: string; statusCode: number }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
				})).finally(() => {
					loading.value = false;
				});
		}

		/** Load all locators for the dropdown */
		const getAllLocators = () => {
			locatorApi.getAllLocators()
				.then(data => {
					allLocators.value = data;
				}).catch(response => response.json().then((err: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ': ' + err.message);
				}));
		}

		/** Mark the current form region in the viewer */
		const markCurrentRegion = () => {
			if (regionAnchor.keyWordRegion) {
				emit("onMarkRegion", [regionAnchor.keyWordRegion]);
			}
		}

		/**
		 * Set marker regions by clicking a row
		 * @param event
		 */
		const onMarkRegion = (event: {originalEvent: PointerEvent; data: any; index: number}) => {
			const row = event.data;
			emit("onMarkRegion", [row.keyWordRegion]);
		}

		onMounted(() => {
			Object.assign(regionAnchor, props.regionAnchorOfFormTraining);
			resetRegionAnchor();
			getAllIndicators();
			getAllLocators();
		})

		onBeforeMount(() => {
			Object.assign(trainingKey, props.trainingKeyField);
		})

		watch(indicatorId, (newVal, oldVal) => {
			if (newVal !== oldVal) {
				getAllRegionAnchors();
			}
		})

		watch(
			() => [props.regionAnchorOfFormTraining.keyWordPattern, props.regionAnchorOfFormTraining.keyWordRegion],
			() => {
				regionAnchor.keyWordPattern = props.regionAnchorOfFormTraining.keyWordPattern;
				regionAnchor.keyWordRegion = props.regionAnchorOfFormTraining.keyWordRegion;
			},
			{ deep: true }
		)

		return {
			t,
			toast,
			trainingKey,
			indicatorId,
			regionAnchor,
			loading,
			allRegionAnchors,
			allIndicators,
			allLocators,
			regionBadge,
			canTrain,
			onFocusField,
			markCurrentRegion,
			resetRegionAnchor,
			trainRegionAnchor,
			testRegionAnchor,
			changeRegionAnchor,
			deleteRegionAnchor,
			getAllIndicators,
			getAllRegionAnchors,
			getAllLocators,
			onMarkRegion,
		}
	},
});
</script>

<style scoped>

.inner-content {
	height: calc(100% - 4.5rem);
}

.region-display {
	background: var(--surface-ground);
	font-family: monospace;
	font-size: 0.85rem;
	display: flex;
	align-items: center;
}

.region-display--set {
	color: var(--green-400);
}

.region-display--clickable {
	cursor: pointer;
}

.region-display--clickable:hover {
	background: var(--surface-hover);
}

.region-display--unset {
	color: var(--red-400);
	font-style: italic;
}

</style>
